export interface IndexField {
  name: string
  type: string
  isKey?: boolean
  isFilterable?: boolean
  isFacetable?: boolean
  isSearchable?: boolean
  isSortable?: boolean
  isHidden?: boolean
  isStored?: boolean
  fields?: IndexField[] | null
}

export interface SearchIndexSchema {
  index: string
  fields: IndexField[]
}

export interface SchemaField {
  path: string
  type: string
  isCollection: boolean
  isFilterable: boolean
  isSortable: boolean
  isSearchable: boolean
  isHidden: boolean
}

function unwrapCollectionType(type: string): string {
  return type.replace(/^Collection\((.*)\)$/i, "$1")
}

export function isCollectionType(type: string): boolean {
  return /^Collection\(/i.test(type)
}

export function isDateTimeOffsetType(type: string): boolean {
  return unwrapCollectionType(type).toLowerCase() === "edm.datetimeoffset"
}

export function isStringType(type: string): boolean {
  return unwrapCollectionType(type).toLowerCase() === "edm.string"
}

export function isLiteralSelectType(type: string): boolean {
  return ["edm.int32", "edm.int64", "edm.double"].includes(unwrapCollectionType(type).toLowerCase())
}

export function flattenSchema(schema: SearchIndexSchema | null | undefined): SchemaField[] | null {
  if (schema == null || !Array.isArray(schema.fields) || schema.fields.length === 0) return null

  const out: SchemaField[] = []

  const walk = (fields: IndexField[], prefix: string, parentIsCollection = false) => {
    for (const f of fields) {
      const path = `${prefix}${f.name}`
      const isCollection = parentIsCollection || isCollectionType(f.type)
      if (f.fields != null && f.fields.length > 0) {
        walk(f.fields, `${path}/`, isCollection)
        continue
      }
      out.push({
        path,
        type: f.type,
        isCollection,
        isFilterable: f.isFilterable === true,
        isSortable: f.isSortable === true,
        isSearchable: f.isSearchable === true,
        isHidden: f.isHidden === true,
      })
    }
  }

  walk(schema.fields, "")
  return out.length > 0 ? out : null
}

export type PathStatus =
  | { kind: "empty" }
  | { kind: "unavailable" }
  | { kind: "valid"; field: SchemaField }
  | { kind: "case-mismatch"; suggestion: SchemaField }
  | { kind: "unknown"; suggestions: SchemaField[] }

export function checkFieldPath(rawPath: string, fields: SchemaField[] | null): PathStatus {
  const path = rawPath.trim()
  if (path.length === 0) return { kind: "empty" }
  if (fields == null) return { kind: "unavailable" }

  const exact = fields.find((f) => f.path === path)
  if (exact != null) return { kind: "valid", field: exact }

  const lower = path.toLowerCase()
  const caseInsensitive = fields.find((f) => !f.isHidden && f.path.toLowerCase() === lower)
  if (caseInsensitive != null) return { kind: "case-mismatch", suggestion: caseInsensitive }

  const suggestions = fields.filter((f) => !f.isHidden && f.path.toLowerCase().includes(lower)).slice(0, 5)
  return { kind: "unknown", suggestions }
}

export const BLOCK_ON_UNKNOWN_PATH = true

export function isBlockingStatus(status: PathStatus): boolean {
  if (!BLOCK_ON_UNKNOWN_PATH) return false
  return status.kind === "case-mismatch" || status.kind === "unknown"
}

export function getCapabilities(status: PathStatus) {
  if (status.kind === "valid") {
    return {
      filterable: status.field.isFilterable,
      sortable: status.field.isSortable,
      searchable: status.field.isSearchable,
    }
  }
  return { filterable: true, sortable: true, searchable: true }
}
